import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { HelpPanel } from '../components/ui/help-panel';
import { aiBuckHelp } from '../lib/helpContent';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Sparkles,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Loader2,
  Zap,
  BookOpen,
  Settings2,
  ListTodo,
  Power,
  PowerOff,
  Landmark,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Filter,
  Search,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import InlineCategorySelect from '../components/InlineCategorySelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(Math.abs(amount));
};

// AI Buck Confidence Pill - follows AI_BUCK_GUIDELINES.md
const ConfidencePill = ({ confidence }) => {
  const percent = Math.round(confidence * 100);
  // High (≥85%): Green - AI Buck is confident
  // Medium (70-84%): Amber - AI Buck suggests reviewing  
  // Low (<70%): Red - AI Buck needs your input
  let color = 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400';
  let label = 'Low';
  
  if (percent >= 85) {
    color = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
    label = 'High';
  } else if (percent >= 70) {
    color = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    label = 'Medium';
  }
  
  return (
    <Badge variant="secondary" className={cn('text-xs', color)} title={`AI Buck Confidence: ${label}`}>
      {percent}%
    </Badge>
  );
};

const ChipBadge = ({ children }) => {
  const isAI = children === 'AI-powered';
  return (
    <Badge 
      variant={isAI ? "default" : "outline"} 
      className={cn(
        "text-xs font-normal",
        isAI && "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"
      )}
    >
      {isAI && <span className="mr-1">&#10024;</span>}
      {children}
    </Badge>
  );
};

// Transaction Status Badge - same as Banking page
const TransactionStatusBadge = ({ status, hasCategoryAssigned }) => {
  const config = {
    pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    for_review: { label: 'For Review', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    categorized: { label: 'Categorized', class: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    matched: { label: 'Matched', class: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
    posted: { label: 'Posted', class: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
    excluded: { label: 'Excluded', class: 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200' },
    uncategorized: { label: 'Uncategorized', class: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  };
  
  // Determine effective status
  let effectiveStatus = status;
  if (status === 'categorized' && !hasCategoryAssigned) {
    effectiveStatus = 'uncategorized';
  }
  if (status === 'for_review' && !hasCategoryAssigned) {
    effectiveStatus = 'uncategorized';
  }
  
  const cfg = config[effectiveStatus] || config.for_review;
  
  return (
    <Badge variant="secondary" className={cn('text-xs font-medium', cfg.class)} data-testid={`status-badge-${effectiveStatus}`}>
      {cfg.label}
    </Badge>
  );
};

// Sortable Column Header Component
const SortableHeader = ({ label, field, currentSort, currentDir, onSort, className, align = 'left', hasFilter, filterActive, onFilterClick }) => {
  const isActive = currentSort === field;
  
  return (
    <TableHead className={cn("whitespace-nowrap", className)}>
      <div className={cn("flex items-center gap-1", align === 'right' && "justify-end")}>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-auto p-0 hover:bg-transparent font-medium text-xs",
              filterActive && "text-primary"
            )}
            onClick={onFilterClick}
          >
            <span>{label}</span>
            {filterActive && (
              <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">1</Badge>
            )}
            <Filter className={cn("w-3 h-3 ml-1", filterActive ? "text-primary" : "text-muted-foreground/50")} />
          </Button>
        ) : (
          <span className="text-xs font-medium">{label}</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-0 hover:bg-transparent cursor-pointer"
          onClick={() => onSort(field)}
        >
          {isActive ? (
            currentDir === 'asc' 
              ? <ChevronUp className="w-3 h-3 text-primary" />
              : <ChevronDown className="w-3 h-3 text-primary" />
          ) : (
            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
          )}
        </Button>
      </div>
    </TableHead>
  );
};

// Status options for filter
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'yellow' },
  { value: 'for_review', label: 'For Review', color: 'blue' },
  { value: 'uncategorized', label: 'Uncategorized', color: 'amber' },
  { value: 'categorized', label: 'Categorized', color: 'purple' },
];

// Confidence options for filter
const CONFIDENCE_OPTIONS = [
  { value: 'high', label: 'High (≥85%)', min: 0.85, max: 1 },
  { value: 'medium', label: 'Medium (70-84%)', min: 0.70, max: 0.85 },
  { value: 'low', label: 'Low (<70%)', min: 0, max: 0.70 },
  { value: 'none', label: 'No Suggestion', min: null, max: null },
];

export default function AIBuck() {
  const { currentWorkspace } = useWorkspace();
  // Honor `?tab=rules` etc. deep-links so other pages can open us directly
  // on a specific tab (e.g., Banking's "Manage rules" button).
  const _initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      if (p && ['queue', 'suggestions', 'rules', 'feedback', 'settings'].includes(p)) {
        return p;
      }
    } catch { /* ignore */ }
    return 'queue';
  })();
  const [activeTab, setActiveTab] = useState(_initialTab);
  const [loading, setLoading] = useState(true);
  
  // Queue items state (all pending transactions)
  const [queueItems, setQueueItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [approvingIds, setApprovingIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  
  // Filter state
  const [vendorFilter, setVendorFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState([]);
  const [confidenceFilter, setConfidenceFilter] = useState([]);
  
  // Filter popover states
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [confidencePopoverOpen, setConfidencePopoverOpen] = useState(false);
  
  // Regenerate state
  const [regenerating, setRegenerating] = useState(false);
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    conditions: { vendor_contains: '' },
    target_account_id: '',
    transaction_type: 'spend',
    is_active: true,
    priority: 10
  });
  const [savingRule, setSavingRule] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState(null);

  // Approval Patterns (a.k.a. "auto-approve identical suggestions")
  // — surfaces vendor→account pairs the user has already approved 2+
  // times so they can bulk-approve every pending suggestion that matches
  // the same pattern in one click, and (when count ≥ 3) promote the
  // pattern to a permanent rule. Backed by GET /ai-buck/rule-suggestions
  // and POST /ai-buck/rule-suggestions/bulk-approve.
  const [rulePatterns, setRulePatterns] = useState([]);
  const [patternBusy, setPatternBusy] = useState({}); // {vendor_pattern: 'approving'|'creating-rule'}
  
  // Settings state — defaults to `suggest_only` so newly-onboarded users
  // see AI categorization assistance immediately after linking their bank
  // (suggestions only; nothing is auto-applied without explicit approval).
  const [settings, setSettings] = useState({
    mode: 'suggest_only',
    enabled_accounts: [],
    auto_apply_confidence_threshold: 0.85
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Accounts for selection
  const [accounts, setAccounts] = useState([]);
  
  // Bank accounts for filtering
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('all');
  
  // Processing state
  const [processing, setProcessing] = useState(false);

  // Inline expand/edit state — txn_id -> bool (expanded) and txn_id -> account_id
  // (the user's chosen override). Lets the user open a row, pick a different
  // GL account for the AI's suggestion, and approve with the correction —
  // which is then fed back to AI Buck's learning loop so future similar
  // transactions are routed to the corrected category with higher confidence.
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [editedAccountByTxn, setEditedAccountByTxn] = useState({});

  const toggleRowExpand = (txnId, defaultAccountId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(txnId)) {
        next.delete(txnId);
      } else {
        next.add(txnId);
        // Seed the editor with the suggested account so users can leave it
        // alone OR change it from a sensible starting point.
        setEditedAccountByTxn(p => ({
          ...p,
          [txnId]: p[txnId] ?? defaultAccountId ?? '',
        }));
      }
      return next;
    });
  };
  
  // Uncategorized transactions count (for auto-process)
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [autoProcessed, setAutoProcessed] = useState(false);

  // Fetch ALL pending transactions (the queue)
  const fetchQueueItems = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id,
        tab: 'pending', // Get all pending/for_review transactions
        page_size: '500',
        sort_by: sortBy,
        sort_dir: sortDir
      });
      
      if (selectedBankAccount && selectedBankAccount !== 'all') {
        params.append('bank_id', selectedBankAccount);
      }
      
      const response = await fetch(
        `${API_URL}/api/banking/transactions-enhanced?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setQueueItems(data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to fetch queue items:', error);
    }
  }, [currentWorkspace, selectedBankAccount, sortBy, sortDir]);

  const fetchSuggestions = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      let url = `${API_URL}/api/ai-buck/suggestions?workspace_id=${currentWorkspace.workspace_id}&status=pending&limit=500`;
      if (selectedBankAccount && selectedBankAccount !== 'all') {
        url += `&bank_account_id=${selectedBankAccount}`;
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, [currentWorkspace, selectedBankAccount]);

  const fetchRules = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/rules?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setRules(data);
      }
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    }
  }, [currentWorkspace]);

  const fetchSettings = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/settings?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, [currentWorkspace]);

  const fetchRulePatterns = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/rule-suggestions?workspace_id=${currentWorkspace.workspace_id}`
      );
      if (response.ok) {
        const data = await response.json();
        setRulePatterns(data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to fetch rule patterns:', error);
    }
  }, [currentWorkspace]);

  const fetchAccounts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts?active_only=true`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  }, [currentWorkspace]);

  // Fetch bank accounts for filtering
  const fetchBankAccounts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/accounts?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        // API returns {accounts: [...]} so extract the array
        setBankAccounts(data?.accounts || data || []);
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    }
  }, [currentWorkspace]);

  // Fetch count of uncategorized For Review transactions
  const fetchUncategorizedCount = useCallback(async () => {
    if (!currentWorkspace) return 0;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banking/uncategorized-count?workspace_id=${currentWorkspace.workspace_id}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setUncategorizedCount(data.count || 0);
        return data.count || 0;
      }
    } catch (error) {
      console.error('Failed to fetch uncategorized count:', error);
    }
    return 0;
  }, [currentWorkspace]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchQueueItems(),
        fetchSuggestions(),
        fetchRules(),
        fetchRulePatterns(),
        fetchSettings(),
        fetchAccounts(),
        fetchBankAccounts(),
        fetchUncategorizedCount()
      ]);
      setLoading(false);
    };
    
    loadData();
  }, [fetchQueueItems, fetchSuggestions, fetchRules, fetchRulePatterns, fetchSettings, fetchAccounts, fetchBankAccounts, fetchUncategorizedCount]);

  const handleApproveSuggestion = async (suggestionId, createRule = false, overrideAccountId = null) => {
    setApprovingIds(prev => [...prev, suggestionId]);
    
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/suggestions/${suggestionId}/approve?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ create_rule: createRule, override_account_id: overrideAccountId }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.was_overridden) {
          toast.success(`Entry #${data.entry_no} created with corrected category — AI will learn from this`);
        } else {
          toast.success(`Entry #${data.entry_no} created`);
        }
        if (createRule && data.rule_id) {
          toast.success('Rule created for future transactions');
          fetchRules();
        }
        // Auto-rule disclosure — the backend silently creates a rule
        // after 3+ similar approvals to the same account. We surface
        // it here so the user knows future matching transactions will
        // be auto-categorized on bank sync.
        if (data.auto_rule_created) {
          toast.success(
            '🎯 Auto-rule created from your repeat pattern · future matching transactions will be categorized automatically on bank sync · view in Rules tab',
            { duration: 8000 },
          );
          fetchRules();
        }
        // Collapse the row on success so the queue cleans up visually
        setExpandedRows(prev => {
          const next = new Set(prev);
          // Find the txn id for this suggestion to collapse it
          const item = filteredAndSortedItems.find(i => i.suggestion_id === suggestionId);
          if (item) next.delete(item.transaction_id);
          return next;
        });
        fetchSuggestions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve suggestion');
      }
    } catch (error) {
      toast.error('Failed to approve suggestion');
    } finally {
      setApprovingIds(prev => prev.filter(id => id !== suggestionId));
    }
  };

  const handleRejectSuggestion = async (suggestionId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/suggestions/${suggestionId}/reject?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          }
      );

      if (response.ok) {
        toast.success('Suggestion rejected');
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Failed to reject suggestion');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedSuggestions.length === 0) return;
    
    setBulkApproving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/suggestions/bulk-approve?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggestion_ids: selectedSuggestions }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved ${data.approved} suggestions`);
        setSelectedSuggestions([]);
        fetchSuggestions();
      }
    } catch (error) {
      toast.error('Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.name || !newRule.target_account_id) {
      toast.error('Name and account are required');
      return;
    }

    setSavingRule(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/rules?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newRule),
        }
      );

      if (response.ok) {
        toast.success('Rule created');
        setRuleDialogOpen(false);
        setNewRule({
          name: '',
          conditions: { vendor_contains: '' },
          target_account_id: '',
          transaction_type: 'spend',
          is_active: true,
          priority: 10
        });
        fetchRules();
      }
    } catch (error) {
      toast.error('Failed to create rule');
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!deleteRuleId) return;

    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/rules/${deleteRuleId}?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'DELETE',
          }
      );

      if (response.ok) {
        toast.success('Rule deleted');
        fetchRules();
      }
    } catch (error) {
      toast.error('Failed to delete rule');
    } finally {
      setDeleteRuleId(null);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/settings?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings),
        }
      );

      if (response.ok) {
        toast.success('Settings saved');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Auto-approve every pending suggestion matching this vendor→account pattern.
  const handleBulkApprovePattern = async (pattern) => {
    setPatternBusy((p) => ({ ...p, [pattern.vendor_pattern]: 'approving' }));
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/rule-suggestions/bulk-approve?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vendor_pattern: pattern.vendor_pattern,
            target_account_id: pattern.suggested_account_id,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.approved > 0) {
          toast.success(
            `Auto-approved ${data.approved} ${pattern.canonical_name || pattern.vendor_pattern} suggestion${data.approved === 1 ? '' : 's'}.`
          );
        } else {
          toast.info(`No pending ${pattern.canonical_name || pattern.vendor_pattern} suggestions to approve.`);
        }
        await Promise.all([fetchSuggestions(), fetchRulePatterns(), fetchUncategorizedCount(), fetchQueueItems()]);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Bulk approve failed');
      }
    } catch (error) {
      toast.error('Bulk approve failed');
    } finally {
      setPatternBusy((p) => {
        const next = { ...p };
        delete next[pattern.vendor_pattern];
        return next;
      });
    }
  };

  // Promote a ready pattern (≥3 approvals) into a permanent bank_rule.
  const handleCreateRuleFromPattern = async (pattern) => {
    setPatternBusy((p) => ({ ...p, [pattern.vendor_pattern]: 'creating-rule' }));
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/rules?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: `Auto-rule: ${pattern.canonical_name || pattern.vendor_pattern}`,
            conditions: { vendor_contains: pattern.vendor_pattern },
            target_account_id: pattern.suggested_account_id,
            transaction_type: 'spend',
            is_active: true,
            priority: 10,
          }),
        }
      );
      if (response.ok) {
        toast.success(`Rule created — every future ${pattern.canonical_name || pattern.vendor_pattern} hit will route automatically.`);
        await Promise.all([fetchRules(), fetchRulePatterns()]);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Rule creation failed');
      }
    } catch (error) {
      toast.error('Rule creation failed');
    } finally {
      setPatternBusy((p) => {
        const next = { ...p };
        delete next[pattern.vendor_pattern];
        return next;
      });
    }
  };

  const handleProcessTransactions = useCallback(async (silent = false) => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/process?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          }
      );

      if (response.ok) {
        const data = await response.json();
        if (!silent) {
          const claudeMsg = data.claude_categorized > 0 ? ` (${data.claude_categorized} AI-powered)` : '';
          toast.success(data.message + claudeMsg);
        }
        fetchQueueItems();
        fetchSuggestions();
        fetchUncategorizedCount();
      }
    } catch (error) {
      if (!silent) {
        toast.error('Processing failed');
      }
    } finally {
      setProcessing(false);
    }
  }, [currentWorkspace, fetchQueueItems, fetchSuggestions, fetchUncategorizedCount]);

  // Regenerate ALL suggestions with enhanced matching
  const handleRegenerateAll = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setRegenerating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai-buck/regenerate-all?workspace_id=${currentWorkspace.workspace_id}&include_categorized=true`,
        {
          method: 'POST',
          }
      );

      if (response.ok) {
        const data = await response.json();
        const { confidence_distribution } = data;
        const highCount = confidence_distribution?.high_gte_85 || 0;
        const medCount = confidence_distribution?.medium_70_84 || 0;
        
        toast.success(
          `Regenerated ${data.suggestions_created} suggestions! ${highCount} high confidence, ${medCount} medium confidence.`,
          { duration: 5000 }
        );
        
        fetchQueueItems();
        fetchSuggestions();
        fetchUncategorizedCount();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Regeneration failed');
      }
    } catch (error) {
      toast.error('Regeneration failed');
    } finally {
      setRegenerating(false);
    }
  }, [currentWorkspace, fetchQueueItems, fetchSuggestions, fetchUncategorizedCount]);

  // Auto-process if many uncategorized transactions and few suggestions
  useEffect(() => {
    const shouldAutoProcess = 
      !autoProcessed && 
      !loading && 
      !processing &&
      settings.mode !== 'off' &&
      queueItems.length > 10 && 
      suggestions.length < queueItems.length * 0.3;
    
    if (shouldAutoProcess) {
      setAutoProcessed(true);
      handleProcessTransactions(true); // silent mode for auto-process
    }
  }, [autoProcessed, loading, processing, settings.mode, queueItems.length, suggestions.length, handleProcessTransactions]);

  const toggleSelectAll = () => {
    if (selectedSuggestions.length === suggestions.length) {
      setSelectedSuggestions([]);
    } else {
      setSelectedSuggestions(suggestions.map(s => s.suggestion_id));
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setVendorFilter('');
    setCategoryFilter('all');
    setStatusFilter([]);
    setConfidenceFilter([]);
  };

  // Check if any filter is active
  const hasActiveFilters = vendorFilter || categoryFilter !== 'all' || statusFilter.length > 0 || confidenceFilter.length > 0;

  // Merge queue items with their suggestions - useMemo for performance
  const mergedQueueItems = useMemo(() => {
    // Create a map of suggestions by transaction_id for fast lookup
    const suggestionMap = new Map();
    suggestions.forEach(sugg => {
      suggestionMap.set(sugg.transaction_id, sugg);
    });
    
    // Merge queue items with their suggestions
    return queueItems.map(txn => {
      const suggestion = suggestionMap.get(txn.transaction_id);
      return {
        ...txn,
        suggestion: suggestion || null,
        suggestion_id: suggestion?.suggestion_id || null,
        suggested_account_code: suggestion?.suggested_account_code || null,
        suggested_account_name: suggestion?.suggested_account_name || null,
        confidence: suggestion?.confidence || null,
        rationale: suggestion?.rationale || null,
        explanation_chips: suggestion?.explanation_chips || [],
        rule_id: suggestion?.rule_id || null,
        // Determine effective status
        effectiveStatus: (!txn.suggested_account_id && !suggestion) 
          ? 'uncategorized' 
          : txn.status
      };
    });
  }, [queueItems, suggestions]);

  // Apply filters and sorting
  const filteredAndSortedItems = useMemo(() => {
    let items = [...mergedQueueItems];
    
    // Apply vendor filter
    if (vendorFilter) {
      const filterLower = vendorFilter.toLowerCase();
      items = items.filter(item => {
        const vendor = (item.merchant_name || item.name || '').toLowerCase();
        return vendor.includes(filterLower);
      });
    }
    
    // Apply category filter
    if (categoryFilter && categoryFilter !== 'all') {
      items = items.filter(item => {
        const accountId = item.suggested_account_id || item.suggestion?.suggested_account_id;
        return accountId === categoryFilter;
      });
    }
    
    // Apply status filter
    if (statusFilter.length > 0) {
      items = items.filter(item => {
        // Determine effective status for filtering
        const hasCategory = item.suggested_account_id || item.suggestion;
        let effectiveStatus = item.status;
        if (!hasCategory) {
          effectiveStatus = 'uncategorized';
        }
        return statusFilter.includes(effectiveStatus) || statusFilter.includes('uncategorized') && !hasCategory;
      });
    }
    
    // Apply confidence filter
    if (confidenceFilter.length > 0) {
      items = items.filter(item => {
        const conf = item.confidence;
        // Handle "no suggestion" filter
        if (confidenceFilter.includes('none') && conf === null) {
          return true;
        }
        if (conf === null) return false;
        
        return confidenceFilter.some(filter => {
          const opt = CONFIDENCE_OPTIONS.find(o => o.value === filter);
          if (!opt || opt.min === null) return false;
          return conf >= opt.min && conf < opt.max;
        });
      });
    }
    
    // Apply sorting
    items.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'date':
          aVal = a.date || '';
          bVal = b.date || '';
          break;
        case 'vendor':
          aVal = (a.merchant_name || a.name || '').toLowerCase();
          bVal = (b.merchant_name || b.name || '').toLowerCase();
          break;
        case 'amount':
          aVal = Math.abs(a.amount || 0);
          bVal = Math.abs(b.amount || 0);
          break;
        case 'category':
          aVal = (a.suggested_account_name || '').toLowerCase();
          bVal = (b.suggested_account_name || '').toLowerCase();
          break;
        case 'confidence':
          aVal = a.confidence || 0;
          bVal = b.confidence || 0;
          break;
        case 'status':
          const statusOrder = { uncategorized: 0, pending: 1, for_review: 2, categorized: 3, posted: 4 };
          aVal = statusOrder[a.effectiveStatus] || 5;
          bVal = statusOrder[b.effectiveStatus] || 5;
          break;
        default:
          aVal = a.date || '';
          bVal = b.date || '';
      }
      
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    
    return items;
  }, [mergedQueueItems, vendorFilter, categoryFilter, statusFilter, confidenceFilter, sortBy, sortDir]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-buck">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/banking">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Auto-Categorizer</h1>
            </div>
            <p className="text-muted-foreground">
              Automatic transaction categorization
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge 
            variant={settings.mode === 'off' ? 'secondary' : 'default'}
            className={cn(
              settings.mode === 'auto_apply' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
              settings.mode === 'suggest_only' && 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
            )}
          >
            {settings.mode === 'off' && <PowerOff className="w-3 h-3 mr-1" />}
            {settings.mode !== 'off' && <Power className="w-3 h-3 mr-1" />}
            {settings.mode === 'off' ? 'Off' : settings.mode === 'suggest_only' ? 'Suggest Only' : 'Auto Apply'}
          </Badge>
          
          {/* Show pending count if there are transactions without suggestions */}
          {queueItems.length > suggestions.length && (
            <Badge variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              {queueItems.length - suggestions.length} need suggestions
            </Badge>
          )}
          
          <Button 
            variant="outline"
            onClick={() => handleProcessTransactions(false)}
            disabled={processing || settings.mode === 'off'}
            data-testid="process-now-btn"
          >
            {processing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Generate All Suggestions</>
            )}
          </Button>
          <HelpPanel {...aiBuckHelp} />
        </div>
      </div>

      {/* Stats Banner */}
      {queueItems.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">AI Buck Queue Status</p>
                <p className="text-sm text-muted-foreground">
                  {queueItems.length} pending transactions • {suggestions.length} with suggestions
                  {suggestions.length > 0 && (
                    <> • <span className="text-emerald-600">{suggestions.filter(s => s.confidence >= 0.85).length} high</span> / <span className="text-amber-600">{suggestions.filter(s => s.confidence >= 0.70 && s.confidence < 0.85).length} medium</span> confidence</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {queueItems.length > suggestions.length && settings.mode !== 'off' && (
                <Button 
                  variant="outline"
                  onClick={() => handleProcessTransactions(false)}
                  disabled={processing || regenerating}
                  data-testid="generate-all-btn"
                >
                  {processing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <>Generate {queueItems.length - suggestions.length} Missing</>
                  )}
                </Button>
              )}
              {suggestions.length > 0 && settings.mode !== 'off' && (
                <Button 
                  onClick={handleRegenerateAll}
                  disabled={processing || regenerating}
                  data-testid="regenerate-all-banner-btn"
                >
                  {regenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regenerating...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Regenerate All</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="queue" data-testid="tab-queue">
            <ListTodo className="w-4 h-4 mr-2" />
            Queue
            {queueItems.length > 0 && (
              <Badge className="ml-2 bg-primary">{queueItems.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <BookOpen className="w-4 h-4 mr-2" />
            Rules
            {rulePatterns.filter(p => p.ready_for_rule).length > 0 && (
              <Badge
                className="ml-2 bg-emerald-500 hover:bg-emerald-500 text-white"
                data-testid="tab-rules-ready-badge"
                title={`${rulePatterns.filter(p => p.ready_for_rule).length} approval pattern${rulePatterns.filter(p => p.ready_for_rule).length === 1 ? ' is' : 's are'} ready to be promoted to a rule`}
              >
                {rulePatterns.filter(p => p.ready_for_rule).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings2 className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Queue Tab */}
        <TabsContent value="queue" className="mt-6 space-y-4">
          {/* Bank Account Filter + Controls */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Bank Account:</Label>
                <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount} data-testid="bank-account-filter">
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Accounts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    {bankAccounts.map(account => (
                      <SelectItem key={account.bank_id || account.account_id} value={account.bank_id || account.account_id}>
                        {account.name} {account.mask && `(••${account.mask})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="w-3 h-3 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { fetchQueueItems(); fetchSuggestions(); }}
                disabled={processing || regenerating}
                data-testid="refresh-suggestions-btn"
              >
                <RefreshCw className={cn("w-4 h-4 mr-2", processing && "animate-spin")} />
                Refresh
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleProcessTransactions(false)}
                disabled={processing || regenerating || settings.mode === 'off'}
                data-testid="generate-suggestions-btn"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate New</>
                )}
              </Button>
              
              <Button 
                variant="default" 
                size="sm"
                onClick={handleRegenerateAll}
                disabled={processing || regenerating || settings.mode === 'off'}
                data-testid="regenerate-all-btn"
                title="Delete all pending suggestions and regenerate with enhanced matching"
              >
                {regenerating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regenerating...</>
                ) : (
                  <><Zap className="w-4 h-4 mr-2" /> Regenerate All</>
                )}
              </Button>
            </div>
          </div>
          
          {/* Bulk Actions Bar */}
          {filteredAndSortedItems.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={selectedSuggestions.length > 0 && selectedSuggestions.length === filteredAndSortedItems.filter(i => i.suggestion_id).length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedSuggestions(filteredAndSortedItems.filter(i => i.suggestion_id).map(i => i.suggestion_id));
                      } else {
                        setSelectedSuggestions([]);
                      }
                    }}
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-sm text-muted-foreground">
                    {selectedSuggestions.length} of {filteredAndSortedItems.filter(i => i.suggestion_id).length} with suggestions selected
                  </span>
                </div>
                
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const threshold = settings.auto_apply_confidence_threshold;
                    const highConfidence = filteredAndSortedItems
                      .filter(i => i.confidence && i.confidence >= threshold)
                      .map(i => i.suggestion_id);
                    setSelectedSuggestions(highConfidence);
                  }}
                  data-testid="select-high-confidence-btn"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Select High Confidence ({filteredAndSortedItems.filter(i => i.confidence && i.confidence >= settings.auto_apply_confidence_threshold).length})
                </Button>
              </div>
              
              {selectedSuggestions.length > 0 && (
                <Button 
                  onClick={handleBulkApprove}
                  disabled={bulkApproving}
                  data-testid="bulk-approve-btn"
                >
                  {bulkApproving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Approve Selected ({selectedSuggestions.length})</>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Queue Summary */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredAndSortedItems.length} transactions 
            {hasActiveFilters && ` (filtered from ${queueItems.length})`}
            {' • '}
            {filteredAndSortedItems.filter(i => i.suggestion_id).length} with suggestions
          </div>

          <Card>
            <CardContent className="p-0">
              {filteredAndSortedItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      
                      {/* Vendor/Description - Sortable + Filterable */}
                      <TableHead className="min-w-[180px]">
                        <Popover open={vendorPopoverOpen} onOpenChange={setVendorPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                                vendorFilter && "text-primary"
                              )}
                              data-testid="vendor-filter-btn"
                            >
                              <span>Vendor / Description</span>
                              {vendorFilter && (
                                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">1</Badge>
                              )}
                              <Filter className={cn("w-3 h-3 ml-1", vendorFilter ? "text-primary" : "text-muted-foreground/50")} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Filter by Vendor</span>
                                {vendorFilter && (
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => { setVendorFilter(''); setVendorPopoverOpen(false); }}>
                                    Clear
                                  </Button>
                                )}
                              </div>
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <Input
                                  value={vendorFilter}
                                  onChange={(e) => setVendorFilter(e.target.value)}
                                  placeholder="Search vendor name..."
                                  className="h-8 pl-7 text-xs"
                                  data-testid="vendor-search-input"
                                />
                              </div>
                              <Button size="sm" className="w-full h-7 text-xs" onClick={() => setVendorPopoverOpen(false)}>
                                Apply
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1 hover:bg-transparent"
                          onClick={() => handleSort('vendor')}
                        >
                          {sortBy === 'vendor' ? (
                            sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                          )}
                        </Button>
                      </TableHead>
                      
                      {/* Amount - Sortable */}
                      <SortableHeader 
                        label="Amount" 
                        field="amount" 
                        currentSort={sortBy} 
                        currentDir={sortDir} 
                        onSort={handleSort}
                        className="w-28 text-right"
                        align="right"
                      />
                      
                      {/* Suggested Category - Sortable + Filterable */}
                      <TableHead className="min-w-[150px]">
                        <Popover open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                                categoryFilter !== 'all' && "text-primary"
                              )}
                              data-testid="category-filter-btn"
                            >
                              <span>Suggested Category</span>
                              {categoryFilter !== 'all' && (
                                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">1</Badge>
                              )}
                              <Filter className={cn("w-3 h-3 ml-1", categoryFilter !== 'all' ? "text-primary" : "text-muted-foreground/50")} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Filter by Category</span>
                                {categoryFilter !== 'all' && (
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => { setCategoryFilter('all'); setCategoryPopoverOpen(false); }}>
                                    Clear
                                  </Button>
                                )}
                              </div>
                              <ScrollArea className="h-[200px]">
                                <div className="space-y-1">
                                  <label
                                    className={cn(
                                      "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50",
                                      categoryFilter === 'all' && "bg-primary/10"
                                    )}
                                    onClick={() => setCategoryFilter('all')}
                                  >
                                    <div className={cn(
                                      "w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center",
                                      categoryFilter === 'all' ? "border-primary bg-primary" : "border-muted-foreground/30"
                                    )}>
                                      {categoryFilter === 'all' && <CheckCircle2 className="w-2 h-2 text-white" />}
                                    </div>
                                    <span className="text-xs">All Categories</span>
                                  </label>
                                  {accounts.filter(a => a.type === 'expense' || a.type === 'income').slice(0, 15).map(account => (
                                    <label
                                      key={account.account_id}
                                      className={cn(
                                        "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50",
                                        categoryFilter === account.account_id && "bg-primary/10"
                                      )}
                                      onClick={() => setCategoryFilter(account.account_id)}
                                    >
                                      <div className={cn(
                                        "w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center",
                                        categoryFilter === account.account_id ? "border-primary bg-primary" : "border-muted-foreground/30"
                                      )}>
                                        {categoryFilter === account.account_id && <CheckCircle2 className="w-2 h-2 text-white" />}
                                      </div>
                                      <span className="text-xs truncate">{account.code} - {account.name}</span>
                                    </label>
                                  ))}
                                </div>
                              </ScrollArea>
                              <Button size="sm" className="w-full h-7 text-xs" onClick={() => setCategoryPopoverOpen(false)}>
                                Apply
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1 hover:bg-transparent"
                          onClick={() => handleSort('category')}
                        >
                          {sortBy === 'category' ? (
                            sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                          )}
                        </Button>
                      </TableHead>
                      
                      {/* Confidence - Sortable + Filterable */}
                      <TableHead className="w-24">
                        <Popover open={confidencePopoverOpen} onOpenChange={setConfidencePopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                                confidenceFilter.length > 0 && "text-primary"
                              )}
                              data-testid="confidence-filter-btn"
                            >
                              <span>Confidence</span>
                              {confidenceFilter.length > 0 && (
                                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">{confidenceFilter.length}</Badge>
                              )}
                              <Filter className={cn("w-3 h-3 ml-1", confidenceFilter.length > 0 ? "text-primary" : "text-muted-foreground/50")} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Filter by Confidence</span>
                                {confidenceFilter.length > 0 && (
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => { setConfidenceFilter([]); setConfidencePopoverOpen(false); }}>
                                    Clear
                                  </Button>
                                )}
                              </div>
                              <div className="space-y-1">
                                {CONFIDENCE_OPTIONS.map(opt => (
                                  <label
                                    key={opt.value}
                                    className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                                  >
                                    <Checkbox
                                      checked={confidenceFilter.includes(opt.value)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setConfidenceFilter(prev => [...prev, opt.value]);
                                        } else {
                                          setConfidenceFilter(prev => prev.filter(v => v !== opt.value));
                                        }
                                      }}
                                    />
                                    <span className={cn(
                                      "text-xs",
                                      opt.value === 'high' && "text-emerald-600",
                                      opt.value === 'medium' && "text-amber-600",
                                      opt.value === 'low' && "text-rose-600",
                                      opt.value === 'none' && "text-gray-500"
                                    )}>
                                      {opt.label}
                                    </span>
                                  </label>
                                ))}
                              </div>
                              <Button size="sm" className="w-full h-7 text-xs" onClick={() => setConfidencePopoverOpen(false)}>
                                Apply
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1 hover:bg-transparent"
                          onClick={() => handleSort('confidence')}
                        >
                          {sortBy === 'confidence' ? (
                            sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                          )}
                        </Button>
                      </TableHead>
                      
                      {/* Explanation - Sortable only */}
                      <SortableHeader 
                        label="Explanation" 
                        field="explanation" 
                        currentSort={sortBy} 
                        currentDir={sortDir} 
                        onSort={handleSort}
                      />
                      
                      {/* Status - Sortable + Filterable */}
                      <TableHead className="w-28">
                        <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-auto p-0 hover:bg-transparent font-medium text-xs",
                                statusFilter.length > 0 && "text-primary"
                              )}
                              data-testid="status-filter-btn"
                            >
                              <span>Status</span>
                              {statusFilter.length > 0 && (
                                <Badge variant="secondary" className="ml-1 text-[9px] px-1 py-0 h-4">{statusFilter.length}</Badge>
                              )}
                              <Filter className={cn("w-3 h-3 ml-1", statusFilter.length > 0 ? "text-primary" : "text-muted-foreground/50")} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Filter by Status</span>
                                {statusFilter.length > 0 && (
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => { setStatusFilter([]); setStatusPopoverOpen(false); }}>
                                    Clear
                                  </Button>
                                )}
                              </div>
                              <div className="space-y-1">
                                {STATUS_OPTIONS.map(status => (
                                  <label
                                    key={status.value}
                                    className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                                  >
                                    <Checkbox
                                      checked={statusFilter.includes(status.value)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setStatusFilter(prev => [...prev, status.value]);
                                        } else {
                                          setStatusFilter(prev => prev.filter(v => v !== status.value));
                                        }
                                      }}
                                    />
                                    <span className={cn(
                                      "text-xs",
                                      status.color === 'yellow' && "text-yellow-600",
                                      status.color === 'blue' && "text-blue-600",
                                      status.color === 'amber' && "text-amber-600",
                                      status.color === 'purple' && "text-purple-600"
                                    )}>
                                      {status.label}
                                    </span>
                                  </label>
                                ))}
                              </div>
                              <Button size="sm" className="w-full h-7 text-xs" onClick={() => setStatusPopoverOpen(false)}>
                                Apply
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1 hover:bg-transparent"
                          onClick={() => handleSort('status')}
                        >
                          {sortBy === 'status' ? (
                            sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                          )}
                        </Button>
                      </TableHead>
                      
                      <TableHead className="w-36"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedItems.map((item) => {
                      const isExpanded = expandedRows.has(item.transaction_id);
                      const editedAccountId =
                        editedAccountByTxn[item.transaction_id] ?? item.suggested_account_id ?? '';
                      const hasOverride =
                        item.suggested_account_id &&
                        editedAccountId &&
                        editedAccountId !== item.suggested_account_id;
                      return (
                      <Fragment key={item.transaction_id}>
                      <TableRow data-testid={`queue-item-${item.transaction_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {item.suggestion_id && (
                              <Checkbox 
                                checked={selectedSuggestions.includes(item.suggestion_id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedSuggestions(prev => [...prev, item.suggestion_id]);
                                  } else {
                                    setSelectedSuggestions(prev => prev.filter(id => id !== item.suggestion_id));
                                  }
                                }}
                              />
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                              onClick={() => toggleRowExpand(item.transaction_id, item.suggested_account_id)}
                              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                              data-testid={`expand-row-${item.transaction_id}`}
                              title="Expand to change category, view details, and create a rule"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronDown className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{item.merchant_name || item.name || item.transaction_id}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.date}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono text-sm",
                          item.amount > 0 ? "text-rose-600" : "text-emerald-600"
                        )}>
                          {item.amount != null ? (
                            <>
                              {item.amount > 0 ? '-' : '+'}
                              {formatCurrency(item.amount)}
                            </>
                          ) : '--'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {/* Inline category dropdown — change the AI's pick
                              directly on the row without expanding. Same
                              component used in Banking for consistent UX.
                              When changed, the value is staged in
                              editedAccountByTxn and applied on Approve. */}
                          <div className="flex items-center gap-2">
                            <InlineCategorySelect
                              value={editedAccountId || ''}
                              accounts={accounts}
                              ghost
                              testId={`row-category-${item.transaction_id}`}
                              onChange={(newId) =>
                                setEditedAccountByTxn(p => ({ ...p, [item.transaction_id]: newId }))
                              }
                            />
                            {hasOverride && (
                              <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:border-amber-700 dark:bg-amber-950/30">
                                Will be corrected
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.confidence != null ? (
                            <ConfidencePill confidence={item.confidence} />
                          ) : (
                            <Badge variant="outline" className="text-xs text-gray-500">--</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {item.explanation_chips?.slice(0, 2).map((chip, i) => (
                              <ChipBadge key={i}>{chip}</ChipBadge>
                            ))}
                            {!item.explanation_chips?.length && (
                              <span className="text-xs text-muted-foreground">--</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TransactionStatusBadge 
                            status={item.status} 
                            hasCategoryAssigned={!!(item.suggested_account_id || item.suggestion)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {item.suggestion_id ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleApproveSuggestion(item.suggestion_id, false)}
                                  disabled={approvingIds.includes(item.suggestion_id)}
                                  data-testid={`approve-${item.suggestion_id}`}
                                  title="Approve suggestion"
                                >
                                  {approvingIds.includes(item.suggestion_id) ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRejectSuggestion(item.suggestion_id)}
                                  data-testid={`reject-${item.suggestion_id}`}
                                  title="Reject suggestion"
                                >
                                  <XCircle className="w-4 h-4 text-destructive" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleApproveSuggestion(item.suggestion_id, true)}
                                  disabled={approvingIds.includes(item.suggestion_id)}
                                  title="Approve and create rule"
                                >
                                  <Zap className="w-3 h-3 mr-1" />
                                  Rule
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleProcessTransactions(false)}
                                disabled={processing}
                                title="Generate suggestion for this transaction"
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                Generate
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail row — inline category override + rule
                          creation. Shown only when the user clicks the chevron
                          on the parent row. Lets users correct a wrong
                          AI suggestion (e.g. "Sales Revenue" → "Transfer")
                          and either Approve or Approve + Create Rule with the
                          override, which is what trains AI Buck for next time. */}
                      {isExpanded && (
                        <TableRow
                          className="bg-emerald-50/30 dark:bg-emerald-950/10"
                          data-testid={`expanded-row-${item.transaction_id}`}
                        >
                          <TableCell colSpan={9} className="p-0">
                            <div className="px-6 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                              {/* Transaction details — full context */}
                              <div className="space-y-2">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                                  Transaction
                                </p>
                                <div className="space-y-1 text-sm">
                                  <div className="font-medium">{item.merchant_name || item.name}</div>
                                  {item.name && item.merchant_name && item.name !== item.merchant_name && (
                                    <div className="text-xs text-muted-foreground italic">
                                      Description: {item.name}
                                    </div>
                                  )}
                                  <div className="text-xs text-muted-foreground">
                                    {item.date} · {item.amount != null ? formatCurrency(item.amount) : '—'}
                                  </div>
                                </div>
                                {item.rationale && (
                                  <p className="text-xs text-muted-foreground italic pt-1">
                                    <span className="font-semibold">Why AI suggested:</span> {item.rationale}
                                  </p>
                                )}
                                {item.explanation_chips?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 pt-1">
                                    {item.explanation_chips.map((chip, i) => (
                                      <ChipBadge key={i}>{chip}</ChipBadge>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Category override — pick the correct GL account */}
                              <div className="space-y-2">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                                  Change category
                                </p>
                                <Select
                                  value={editedAccountId || undefined}
                                  onValueChange={(val) =>
                                    setEditedAccountByTxn(p => ({ ...p, [item.transaction_id]: val }))
                                  }
                                >
                                  <SelectTrigger className="h-9 text-sm" data-testid={`override-account-${item.transaction_id}`}>
                                    <SelectValue placeholder="Select account" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-72">
                                    {accounts.map((a) => (
                                      <SelectItem key={a.account_id} value={a.account_id}>
                                        <span className="font-mono text-xs mr-2">{a.code}</span>
                                        {a.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {hasOverride && (
                                  <p className="text-[11px] text-amber-700 dark:text-amber-400">
                                    AI Buck will learn from this correction. Future similar transactions will trend toward this category.
                                  </p>
                                )}
                              </div>

                              {/* Actions — approve with override, +rule, cancel */}
                              <div className="space-y-2">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                                  Actions
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      handleApproveSuggestion(
                                        item.suggestion_id,
                                        false,
                                        hasOverride ? editedAccountId : null,
                                      )
                                    }
                                    disabled={approvingIds.includes(item.suggestion_id) || !item.suggestion_id}
                                    data-testid={`save-approve-${item.transaction_id}`}
                                    className="gap-1.5"
                                  >
                                    {approvingIds.includes(item.suggestion_id) ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                    )}
                                    {hasOverride ? 'Save & Approve' : 'Approve'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleApproveSuggestion(
                                        item.suggestion_id,
                                        true,
                                        hasOverride ? editedAccountId : null,
                                      )
                                    }
                                    disabled={approvingIds.includes(item.suggestion_id) || !item.suggestion_id}
                                    data-testid={`approve-with-rule-${item.transaction_id}`}
                                    className="gap-1.5"
                                  >
                                    <Zap className="w-3.5 h-3.5" />
                                    Approve + Create Rule
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleRowExpand(item.transaction_id)}
                                    data-testid={`collapse-row-${item.transaction_id}`}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                                <p className="text-[11px] text-muted-foreground pt-1">
                                  "Approve + Create Rule" will route every future "{item.merchant_name || item.name}" transaction to the chosen category automatically.
                                </p>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No pending transactions</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    All transactions have been categorized or posted
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="mt-6 space-y-4">
          {/* Approval Patterns — vendors the user has approved to the
              same account 2+ times. Auto-approve identical pending hits or
              promote ready patterns to a permanent rule. */}
          {rulePatterns.length > 0 && (
            <Card
              className="border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20"
              data-testid="ai-buck-approval-patterns"
            >
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  Approval Patterns
                </CardTitle>
                <CardDescription>
                  Vendors you've approved to the same account before. One-click
                  approve every matching pending suggestion — or promote a
                  pattern to a permanent rule once you've approved it 3+ times.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {rulePatterns.map((pattern) => {
                  const busy = patternBusy[pattern.vendor_pattern];
                  const ready = pattern.ready_for_rule;
                  return (
                    <div
                      key={`${pattern.vendor_pattern}-${pattern.suggested_account_id}`}
                      className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 rounded-lg bg-white dark:bg-zinc-900 ring-1 ring-zinc-200 dark:ring-white/10"
                      data-testid={`approval-pattern-${pattern.vendor_pattern}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm capitalize">
                            {pattern.canonical_name || pattern.vendor_pattern}
                          </span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[10px]',
                              ready
                                ? 'border-emerald-400 text-emerald-700 dark:text-emerald-400'
                                : 'border-amber-400 text-amber-700 dark:text-amber-400',
                            )}
                          >
                            {pattern.approval_count} approval{pattern.approval_count === 1 ? '' : 's'}
                          </Badge>
                          {ready ? (
                            <Badge className="text-[10px] bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              Ready for rule
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">
                              {3 - pattern.approval_count} more to unlock rule
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          → <span className="font-mono">{pattern.suggested_account_code}</span>{' '}
                          {pattern.suggested_account_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkApprovePattern(pattern)}
                          disabled={!!busy}
                          data-testid={`bulk-approve-${pattern.vendor_pattern}`}
                        >
                          {busy === 'approving' ? (
                            <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Approving…</>
                          ) : (
                            <><CheckCircle2 className="w-3 h-3 mr-1.5" /> Auto-approve matching</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleCreateRuleFromPattern(pattern)}
                          disabled={!ready || !!busy}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white"
                          data-testid={`promote-to-rule-${pattern.vendor_pattern}`}
                        >
                          {busy === 'creating-rule' ? (
                            <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Creating…</>
                          ) : (
                            <><Plus className="w-3 h-3 mr-1.5" /> Make a rule</>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end">
            <Button onClick={() => setRuleDialogOpen(true)} data-testid="create-rule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Rule
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {rules.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Conditions</TableHead>
                      <TableHead>Target Account</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rules.map((rule) => (
                      <TableRow key={rule.rule_id} data-testid={`rule-${rule.rule_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            {rule.auto_generated && (
                              <Badge variant="outline" className="text-xs mt-1">
                                <Sparkles className="w-3 h-3 mr-1" />
                                Auto-generated
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {rule.conditions?.vendor_contains && (
                              <ChipBadge>Vendor: {rule.conditions.vendor_contains}</ChipBadge>
                            )}
                            {rule.conditions?.description_contains && (
                              <ChipBadge>Desc: {rule.conditions.description_contains}</ChipBadge>
                            )}
                            {rule.conditions?.amount_range && (
                              <ChipBadge>
                                Amount: {formatCurrency(rule.conditions.amount_range[0])} - {formatCurrency(rule.conditions.amount_range[1])}
                              </ChipBadge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{rule.target_account_code}</span>{' '}
                          {rule.target_account_name}
                        </TableCell>
                        <TableCell className="capitalize">{rule.transaction_type}</TableCell>
                        <TableCell>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                            {rule.is_active ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteRuleId(rule.rule_id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No rules created</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Rules automatically categorize recurring transactions
                  </p>
                  <Button variant="outline" className="mt-4" onClick={() => setRuleDialogOpen(true)}>
                    Create Your First Rule
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Buck Mode</CardTitle>
              <CardDescription>
                Control how AI Buck processes transactions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { value: 'off', label: 'Off', desc: 'Disable AI Buck', icon: PowerOff },
                  { value: 'suggest_only', label: 'Suggest Only', desc: 'Queue for review', icon: ListTodo },
                  { value: 'auto_apply', label: 'Auto Apply', desc: 'Auto-categorize (still requires post)', icon: Zap },
                ].map(({ value, label, desc, icon: Icon }) => (
                  <div
                    key={value}
                    className={cn(
                      "p-4 rounded-lg border-2 cursor-pointer transition-colors",
                      settings.mode === value 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-muted-foreground/50"
                    )}
                    onClick={() => setSettings({ ...settings, mode: value })}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        settings.mode === value ? "bg-primary/10" : "bg-muted"
                      )}>
                        <Icon className={cn(
                          "w-5 h-5",
                          settings.mode === value ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      <div>
                        <p className="font-medium">{label}</p>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Auto-Apply Confidence Threshold: {Math.round((settings.auto_apply_confidence_threshold || 0.85) * 100)}%</Label>
                  <Slider
                    value={[(settings.auto_apply_confidence_threshold || 0.85) * 100]}
                    onValueChange={([v]) => setSettings({ ...settings, auto_apply_confidence_threshold: v / 100 })}
                    min={50}
                    max={100}
                    step={5}
                    className="max-w-md"
                  />
                  <p className="text-xs text-muted-foreground">
                    Only auto-apply suggestions with confidence above this threshold
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleSaveSettings} 
                disabled={savingSettings}
                data-testid="save-settings-btn"
              >
                {savingSettings ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Rule Dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Categorization Rule</DialogTitle>
            <DialogDescription>
              Define conditions to automatically categorize matching transactions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule Name</Label>
              <Input
                id="rule-name"
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                placeholder="e.g., Starbucks Coffee"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor-contains">Vendor Contains</Label>
              <Input
                id="vendor-contains"
                value={newRule.conditions.vendor_contains}
                onChange={(e) => setNewRule({ 
                  ...newRule, 
                  conditions: { ...newRule.conditions, vendor_contains: e.target.value }
                })}
                placeholder="e.g., starbucks"
              />
            </div>

            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <Select 
                value={newRule.transaction_type}
                onValueChange={(v) => setNewRule({ ...newRule, transaction_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spend">Spend</SelectItem>
                  <SelectItem value="receive">Receive</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Target Account</Label>
              <Select 
                value={newRule.target_account_id}
                onValueChange={(v) => setNewRule({ ...newRule, target_account_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.account_id} value={account.account_id}>
                      <span className="font-mono text-xs">{account.code}</span> - {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={newRule.is_active}
                onCheckedChange={(v) => setNewRule({ ...newRule, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRule}
              disabled={savingRule || !newRule.name || !newRule.target_account_id}
            >
              {savingRule ? 'Creating...' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Rule Confirmation */}
      <AlertDialog open={!!deleteRuleId} onOpenChange={() => setDeleteRuleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this categorization rule. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRule}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
